import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token refresh mutex to prevent concurrent refresh attempts
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retrying, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't attempt token refresh for auth/login endpoints — 401 there means bad credentials,
      // not an expired token. Let the calling code handle the error directly.
      const url: string = originalRequest.url || '';
      if (url.includes('/auth/user/login') || url.includes('/auth/admin/login') || url.includes('/auth/verify-otp')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          }, {
            headers: { Authorization: `Bearer ${refreshToken}` },
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data?.data || response.data;
          
          localStorage.setItem('accessToken', accessToken);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          // Update Zustand auth store reactively
          try {
            const authStorage = localStorage.getItem('auth-storage');
            if (authStorage) {
              const parsed = JSON.parse(authStorage);
              parsed.state.token = accessToken;
              parsed.state.isAuthenticated = true;
              localStorage.setItem('auth-storage', JSON.stringify(parsed));
            }
          } catch {
            // Ignore parse errors
          }

          // Also update the Zustand store in-memory if possible
          try {
            const { useAuthStore } = await import('../stores/authStore');
            useAuthStore.setState({ token: accessToken, isAuthenticated: true });
          } catch {
            // Ignore if store not available
          }

          isRefreshing = false;
          processQueue(null, accessToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(refreshError, null);
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          // Clear Zustand auth state to prevent ProtectedRoute from keeping user in
          try {
            const authStorage = localStorage.getItem('auth-storage');
            if (authStorage) {
              const parsed = JSON.parse(authStorage);
              parsed.state.token = null;
              parsed.state.isAuthenticated = false;
              parsed.state.user = null;
              localStorage.setItem('auth-storage', JSON.stringify(parsed));
            }
          } catch { /* ignore */ }
          try {
            const { useAuthStore } = await import('../stores/authStore');
            useAuthStore.setState({ token: null, isAuthenticated: false, user: null });
          } catch { /* ignore */ }
          window.location.href = '/login';
        }
      } else {
        isRefreshing = false;
        // No refresh token, redirect to login
        localStorage.removeItem('accessToken');
        // Clear Zustand auth state
        try {
          const authStorage = localStorage.getItem('auth-storage');
          if (authStorage) {
            const parsed = JSON.parse(authStorage);
            parsed.state.token = null;
            parsed.state.isAuthenticated = false;
            parsed.state.user = null;
            localStorage.setItem('auth-storage', JSON.stringify(parsed));
          }
        } catch { /* ignore */ }
        try {
          const { useAuthStore } = await import('../stores/authStore');
          useAuthStore.setState({ token: null, isAuthenticated: false, user: null });
        } catch { /* ignore */ }
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Helper functions
export const extractData = <T>(response: any): T => {
  return response.data?.data?.data || response.data?.data || response.data;
};

export const handleApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};
